import { type Context, type ReactNode, createContext, useContext } from "react";

export type TSplitContext<T extends object> = {
  [K in keyof T]: Context<T[K] | undefined>;
};

export const createSplitContext = <T extends object>(initialState: Partial<T>) => {
  const contextsMap = {} as TSplitContext<T>;
  for (const key in initialState) {
    contextsMap[key] = createContext<T[typeof key] | undefined>(initialState[key]);
  }
  return contextsMap;
};

export const useSplitContext = <K extends keyof T, T extends object>(
  contextsMap: TSplitContext<T>,
  key: K
): Exclude<T[K], undefined> => {
  const context = contextsMap[key];
  if (!context) {
    throw new Error(`Context not found for key: ${String(key)}`);
  }
  const value = useContext(context);
  if (value === undefined) {
    throw new Error("useSplitContext is not within a Provider or value is undefined");
  }
  return value as Exclude<T[K], undefined>;
};

export interface ISplitContextProviderProps<T extends object> {
  contextsMap: TSplitContext<T>;
  values: T;
  children: ReactNode;
}

export const SplitContextProvider = <T extends object>({
  contextsMap,
  values,
  children,
}: ISplitContextProviderProps<T>) => {
  let content = children;
  for (const key in contextsMap) {
    const Context = contextsMap[key];
    content = <Context.Provider value={values[key]}>{content}</Context.Provider>;
  }
  return content;
};
